import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Card,
  CardBody,
  CardHeader,
  Col,
  Label,
  Input,
  FormFeedback,
  Spinner,
  Form,
  Button,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Flatpickr from "react-flatpickr";

import { BreadCrumb } from "../../components/Common";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import {
  State,
  SendNotificationRequest,
  TemplateDto,
  ParsedError,
  NotificationTargetingRequest,
} from "../../interfaces";
import {
  ErrorHandlingTypeEnum,
  NotificationAudienceEnum,
  NotificationMethodEnum,
  NotificationPurchaseFilterEnum,
  OccurrenceEnum,
} from "../../enums";
import {
  useGetNotificationReceiversCountQuery,
  useSendNotificationMutation,
} from "../../store/api/notificationApi";
import { useGetEducationSystemSelectListQuery } from "../../store/api/educationSystemApi";
import { useGetCurriculumSelectListQuery } from "../../store/api/curriculumApi";
import { useGetCourseSelectListQuery } from "../../store/api/courseApi";
import { useGetEducationalStageSelectListQuery } from "../../store/api/educationalStageApi";
import { useGetSubjectsSelectListByGradeQuery } from "../../store/api/subjectApi";
import { Link, useHistory } from "react-router-dom";
import { TemplateSelect } from "../../components";
import { toast } from "react-toastify";
import { SelectListDto } from "../../models";
// @ts-ignore
import "./NotifyUsers.scss";

const APPLICATION_AUDIENCES = [
  NotificationAudienceEnum.STUDENT,
  NotificationAudienceEnum.PARENT,
];

const audienceGroupOf = (value: NotificationAudienceEnum) =>
  APPLICATION_AUDIENCES.includes(value) ? "application" : "administration";

const labelOf = (
  item:
    | {
        value?: { en?: string; ar?: string };
        name?: { en?: string; ar?: string };
      }
    | undefined,
  langKey: "en" | "ar",
) =>
  item?.value?.[langKey] ||
  item?.value?.en ||
  item?.name?.[langKey] ||
  item?.name?.en ||
  "";

const NotifyUsersPage = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const langKey = i18n.language === "ar" ? "ar" : "en";
  const [
    sendNotification,
    {
      isLoading: isLoading,
      error: error,
      isError: isError,
      reset: resetSendNotification,
    },
  ] = useSendNotificationMutation();

  document.title = `${t("documentTitle", { pageName: "notifyUsers" })}`;
  const [nowOrLater, setNowOrLater] = useState<OccurrenceEnum>(
    OccurrenceEnum.NOW,
  );
  const [showConfirm, setShowConfirm] = useState(false);

  const [sendMethod, setSendMethod] = useState<NotificationMethodEnum>(
    NotificationMethodEnum.PUSH_NOTIFICATION,
  );
  const [selectedTemplate, setSelectedTemplate] = useState<any>();
  const [educationSystemId, setEducationSystemId] = useState("");
  const [curriculumId, setCurriculumId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [educationalStageId, setEducationalStageId] = useState("");
  const [gradeId, setGradeId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [purchaseFilter, setPurchaseFilter] =
    useState<NotificationPurchaseFilterEnum>(
      NotificationPurchaseFilterEnum.ALL,
    );

  const { response: templates } = useSelector(
    (state: any): State<TemplateDto[]> => state.template.templates,
  );

  const { data: educationSystems } = useGetEducationSystemSelectListQuery();
  const { data: curricula } = useGetCurriculumSelectListQuery(
    educationSystemId || undefined,
    { skip: !educationSystemId },
  );
  const { data: courses } = useGetCourseSelectListQuery();
  const { data: educationalStages } = useGetEducationalStageSelectListQuery(
    {
      curriculumId: curriculumId || undefined,
      courseId: courseId || undefined,
    },
    { skip: !curriculumId },
  );
  const selectedStageGrades =
    educationalStages?.find((s) => s.id === educationalStageId)?.children || [];
  const { data: gradeSubjects = [] } = useGetSubjectsSelectListByGradeQuery(
    gradeId,
    { skip: !gradeId },
  );

  const notificationForm = useFormik<SendNotificationRequest>({
    enableReinitialize: true,
    initialValues: {
      audience: [],
      title: "",
      arabicTitle: "",
      body: "",
      arabicBody: "",
      scheduleDate: undefined,
      occurrence: OccurrenceEnum.NOW,
      method: NotificationMethodEnum.PUSH_NOTIFICATION,
      templateId: null,
      purchaseFilter: NotificationPurchaseFilterEnum.ALL,
    },
    validationSchema: Yup.object<SendNotificationRequest>({
      audience: Yup.array()
        .min(
          1,
          t("minLengthArray", { minLengthArray: 1, displayName: "audience" }),
        )
        .required(
          t("isNotEmpty", {
            displayName: "audience",
          }),
        ),
      title: Yup.string().when("method", {
        is: NotificationMethodEnum.PUSH_NOTIFICATION,
        then: (schema) =>
          schema
            .required(t("isNotEmpty", { displayName: "title" }))
            .max(50, t("maxLength", { maxLength: 50, displayName: "title" })),
      }),
      arabicTitle: Yup.string().when("method", {
        is: NotificationMethodEnum.PUSH_NOTIFICATION,
        then: (schema) =>
          schema
            .required(t("isNotEmpty", { displayName: "arabicTitle" }))
            .max(
              50,
              t("maxLength", { maxLength: 50, displayName: "arabicTitle" }),
            ),
      }),
      body: Yup.string().when("method", {
        is: NotificationMethodEnum.PUSH_NOTIFICATION,
        then: (schema) =>
          schema
            .required(t("isNotEmpty", { displayName: "body" }))
            .max(255, t("maxLength", { maxLength: 255, displayName: "body" })),
      }),
      arabicBody: Yup.string().when("method", {
        is: NotificationMethodEnum.PUSH_NOTIFICATION,
        then: (schema) =>
          schema
            .required(t("isNotEmpty", { displayName: "arabicBody" }))
            .max(
              255,
              t("maxLength", { maxLength: 255, displayName: "arabicBody" }),
            ),
      }),
      method: Yup.string().required(t("isNotEmpty", { displayName: "method" })),
      templateId: Yup.string()
        .nullable()
        .when("method", {
          is: NotificationMethodEnum.WHATSAPP,
          then: (schema) =>
            schema.required(t("isNotEmpty", { displayName: "template" })),
          otherwise: (schema) => schema.nullable().notRequired(),
        }),
      scheduleDate: Yup.string().required(
        t("isNotEmpty", {
          displayName: " ",
        }),
      ),
    }),

    onSubmit: async (values) => {
      const audience = (values.audience || []).filter(Boolean);
      if (!audience.length) return;
      try {
        await sendNotification({
          ...values,
          ...buildTargeting(audience),
          occurrence: nowOrLater,
          method: sendMethod,
          templateId:
            sendMethod === NotificationMethodEnum.WHATSAPP
              ? values.templateId
              : null,
        }).unwrap();
        toast.success(t("notificationSentSuccessfully"), {
          autoClose: 3000,
        });
        resetSendNotification();
        notificationForm.resetForm();
        clearTargetingFilters();
        setShowConfirm(false);
        setTimeout(() => {
          history.push("/notifications/list");
        }, 1000);
      } catch (err) {
        setShowConfirm(false);
      }
    },
  });

  const audienceValues = (notificationForm.values.audience ||
    []) as NotificationAudienceEnum[];
  const showsPurchaseFilter = audienceValues.some((value) =>
    [
      NotificationAudienceEnum.STUDENT,
      NotificationAudienceEnum.PARENT,
    ].includes(value),
  );
  const showsHierarchyFilters = audienceValues.some((value) =>
    [
      NotificationAudienceEnum.STUDENT,
      NotificationAudienceEnum.PARENT,
      NotificationAudienceEnum.TEACHER_ADMIN,
    ].includes(value),
  );
  const showsSubjectFilter = audienceValues.includes(
    NotificationAudienceEnum.TEACHER_ADMIN,
  );

  const buildTargeting = (
    audience: string[] = audienceValues,
  ): NotificationTargetingRequest => ({
    audience,
    ...(educationSystemId ? { educationSystemId } : {}),
    ...(showsHierarchyFilters && curriculumId ? { curriculumId } : {}),
    ...(showsHierarchyFilters && courseId ? { courseId } : {}),
    ...(showsHierarchyFilters && educationalStageId
      ? { educationalStageId }
      : {}),
    ...(showsHierarchyFilters && gradeId ? { gradeId } : {}),
    ...(showsSubjectFilter && subjectId ? { subjectId } : {}),
    purchaseFilter: showsPurchaseFilter
      ? purchaseFilter
      : NotificationPurchaseFilterEnum.ALL,
  });

  const targetingQuery = useMemo<NotificationTargetingRequest>(
    () => ({
      audience: audienceValues,
      ...(educationSystemId ? { educationSystemId } : {}),
      ...(showsHierarchyFilters && curriculumId ? { curriculumId } : {}),
      ...(showsHierarchyFilters && courseId ? { courseId } : {}),
      ...(showsHierarchyFilters && educationalStageId
        ? { educationalStageId }
        : {}),
      ...(showsHierarchyFilters && gradeId ? { gradeId } : {}),
      ...(showsSubjectFilter && subjectId ? { subjectId } : {}),
      purchaseFilter: showsPurchaseFilter
        ? purchaseFilter
        : NotificationPurchaseFilterEnum.ALL,
    }),
    [
      audienceValues,
      educationSystemId,
      curriculumId,
      courseId,
      educationalStageId,
      gradeId,
      subjectId,
      purchaseFilter,
      showsPurchaseFilter,
      showsHierarchyFilters,
      showsSubjectFilter,
    ],
  );

  const {
    data: receiversCountData,
    isFetching: isCountingReceivers,
    isError: isReceiversCountError,
  } = useGetNotificationReceiversCountQuery(targetingQuery, {
    skip: audienceValues.length === 0,
  });

  const receiversCount =
    audienceValues.length === 0 ? 0 : (receiversCountData?.count ?? 0);

  useEffect(() => {
    console.log();
  }, []);

  const clearTargetingFilters = () => {
    setEducationSystemId("");
    setCurriculumId("");
    setCourseId("");
    setEducationalStageId("");
    setGradeId("");
    setSubjectId("");
    setPurchaseFilter(NotificationPurchaseFilterEnum.ALL);
  };

  const toggleAudience = (value: NotificationAudienceEnum) => {
    const current = audienceValues;
    if (current.includes(value)) {
      notificationForm.setFieldValue(
        "audience",
        current.filter((item) => item !== value),
      );
      return;
    }
    const group = audienceGroupOf(value);
    notificationForm.setFieldValue("audience", [
      ...current.filter((item) => audienceGroupOf(item) === group),
      value,
    ]);
  };

  useEffect(() => {
    if (showsHierarchyFilters) return;
    setCurriculumId("");
    setCourseId("");
    setEducationalStageId("");
    setGradeId("");
  }, [showsHierarchyFilters]);

  useEffect(() => {
    if (showsSubjectFilter) return;
    setSubjectId("");
  }, [showsSubjectFilter]);

  useEffect(() => {
    if (showsPurchaseFilter) return;
    setPurchaseFilter(NotificationPurchaseFilterEnum.ALL);
  }, [showsPurchaseFilter]);

  const onRequestSend = async () => {
    const errors = await notificationForm.validateForm();
    notificationForm.setTouched(
      {
        audience: true,
        title: true,
        arabicTitle: true,
        body: true,
        arabicBody: true,
        templateId: true,
        scheduleDate: true,
        method: true,
      },
      true,
    );
    if (Object.keys(errors).length > 0) return;
    if (receiversCount <= 0) return;
    setShowConfirm(true);
  };

  useEffect(() => {
    notificationForm.setFieldValue("method", sendMethod);
  }, [sendMethod]);

  useEffect(() => {
    if (nowOrLater === OccurrenceEnum.NOW) {
      notificationForm.setFieldValue("scheduleDate", new Date());
    }
  }, [nowOrLater]);

  useEffect(() => {
    notificationForm.setFieldValue("purchaseFilter", purchaseFilter);
  }, [purchaseFilter]);

  useEffect(() => {
    if (sendMethod === NotificationMethodEnum.WHATSAPP) {
      const _chooseTemplate = templates?.find(
        (item) => item.sid === selectedTemplate?.value,
      );
      if (_chooseTemplate?.types["twilio/text"]) {
        notificationForm.setFieldValue(
          "body",
          _chooseTemplate?.types["twilio/text"]["body"],
        );
      }
      if (_chooseTemplate?.types["twilio/call-to-action"]) {
        notificationForm.setFieldValue(
          "body",
          _chooseTemplate?.types["twilio/call-to-action"]["body"],
        );
      }
      notificationForm.setFieldValue("templateId", _chooseTemplate?.sid);
      return;
    }

    notificationForm.setFieldValue("templateId", null);
    notificationForm.setFieldTouched("templateId", false);
  }, [selectedTemplate, sendMethod]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoading && isError) _error = error as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      notificationForm.setErrors(_error.error);
    }
  }, [isLoading, isError]);

  const onChangeDate = (event: any) => {
    if (event.length > 0) {
      const _scheduleDate = event[0];
      notificationForm.setFieldValue("scheduleDate", _scheduleDate);
    }
  };

  const audienceGroups = [
    {
      id: "application",
      label: t("appUsers"),
      options: [
        {
          value: NotificationAudienceEnum.STUDENT,
          label: t("students"),
          icon: "ri-user-follow-line",
        },
        {
          value: NotificationAudienceEnum.PARENT,
          label: t("parents"),
          icon: "ri-parent-line",
        },
      ],
    },
    {
      id: "administration",
      label: t("cpUsers"),
      options: [
        {
          value: NotificationAudienceEnum.SYSTEM_ADMIN,
          label: t("systemAdmins"),
          icon: "ri-shield-user-line",
        },
        {
          value: NotificationAudienceEnum.HEAD_TEACHER_ADMIN,
          label: t("headTeacherAdmins"),
          icon: "ri-user-settings-line",
        },
        {
          value: NotificationAudienceEnum.TEACHER_ADMIN,
          label: t("teacherAdmins"),
          icon: "ri-user-star-line",
        },
      ],
    },
  ];

  const hasFilters = !!(
    educationSystemId ||
    curriculumId ||
    courseId ||
    educationalStageId ||
    gradeId ||
    subjectId ||
    (showsPurchaseFilter &&
      purchaseFilter !== NotificationPurchaseFilterEnum.ALL)
  );

  return (
    <React.Fragment>
      <div className="page-content notify-users-page">
        <Container fluid>
          <BreadCrumb title={t("notifyUsers")} pageTitle={t("notifications")} />
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              return false;
            }}
            action="#"
          >
            <Row>
              <Col xl={5}>
                <Card className="shadow-sm">
                  <CardHeader className="bg-light">
                    <h5 className="card-title mb-1">
                      <i className="ri-group-line me-2 align-middle"></i>
                      {t("notificationTargeting")}
                    </h5>
                    <p className="text-muted mb-0 fs-13">
                      {t("targetingHint")}
                    </p>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-4">
                      <Label className="form-label">
                        {t("targetRecipients")}
                        <span className="text-danger">*</span>
                      </Label>
                      <div className="d-flex flex-column gap-3">
                        {audienceGroups.map((group) => {
                          const selectedGroup = audienceValues.length
                            ? audienceGroupOf(audienceValues[0])
                            : null;
                          const groupLocked =
                            !!selectedGroup && selectedGroup !== group.id;
                          return (
                          <div
                            key={group.id}
                            className={groupLocked ? "opacity-50" : undefined}
                          >
                            <div className="text-muted text-uppercase fs-11 fw-semibold mb-2">
                              {group.label}
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                              {group.options.map((option) => {
                                const selected = audienceValues.includes(
                                  option.value,
                                );
                                return (
                                  <Button
                                    key={option.value}
                                    type="button"
                                    color="secondary"
                                    outline={!selected}
                                    disabled={groupLocked}
                                    className={`notify-choice-btn d-flex align-items-center${
                                      selected ? " is-selected" : ""
                                    }`}
                                    onClick={() =>
                                      toggleAudience(option.value)
                                    }
                                  >
                                    <i className={`${option.icon} me-1`}></i>
                                    {option.label}
                                  </Button>
                                );
                              })}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                      {notificationForm.errors.audience ? (
                        <FormFeedback
                          type="invalid"
                          style={{ display: "block" }}
                        >
                          {notificationForm.errors.audience as string}
                        </FormFeedback>
                      ) : null}
                    </div>

                    <Row className="g-3">
                      <Col md={6}>
                        <Label className="form-label mb-1">
                          {t("educationSystem")}
                        </Label>
                        <Input
                          type="select"
                          value={educationSystemId}
                          onChange={(e) => {
                            setEducationSystemId(e.target.value);
                            setCurriculumId("");
                            setEducationalStageId("");
                            setGradeId("");
                            setSubjectId("");
                          }}
                        >
                          <option value="">{t("all")}</option>
                          {(educationSystems || []).map(
                            (item: SelectListDto) => (
                              <option key={item.id} value={item.id}>
                                {labelOf(item, langKey)}
                              </option>
                            ),
                          )}
                        </Input>
                      </Col>
                      {showsHierarchyFilters && (
                        <>
                      <Col md={6}>
                        <Label className="form-label mb-1">
                          {t("curriculumItem")}
                        </Label>
                        <Input
                          type="select"
                          value={curriculumId}
                          disabled={!educationSystemId}
                          onChange={(e) => {
                            setCurriculumId(e.target.value);
                            setEducationalStageId("");
                            setGradeId("");
                            setSubjectId("");
                          }}
                        >
                          <option value="">{t("all")}</option>
                          {(curricula || []).map((item: any) => (
                            <option key={item.id} value={item.id}>
                              {labelOf(item, langKey)}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={6}>
                        <Label className="form-label mb-1">{t("course")}</Label>
                        <Input
                          type="select"
                          value={courseId}
                          onChange={(e) => {
                            setCourseId(e.target.value);
                            setEducationalStageId("");
                            setGradeId("");
                            setSubjectId("");
                          }}
                        >
                          <option value="">{t("all")}</option>
                          {(courses || []).map((item: SelectListDto) => (
                            <option key={item.id} value={item.id}>
                              {labelOf(item, langKey)}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={6}>
                        <Label className="form-label mb-1">
                          {t("educationalStage")}
                        </Label>
                        <Input
                          type="select"
                          value={educationalStageId}
                          disabled={!curriculumId}
                          onChange={(e) => {
                            setEducationalStageId(e.target.value);
                            setGradeId("");
                            setSubjectId("");
                          }}
                        >
                          <option value="">{t("all")}</option>
                          {(educationalStages || []).map((stage) => (
                            <option key={stage.id} value={stage.id}>
                              {labelOf(stage, langKey)}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={6}>
                        <Label className="form-label mb-1">{t("grade")}</Label>
                        <Input
                          type="select"
                          value={gradeId}
                          disabled={!educationalStageId}
                          onChange={(e) => {
                            setGradeId(e.target.value);
                            setSubjectId("");
                          }}
                        >
                          <option value="">{t("all")}</option>
                          {selectedStageGrades.map((grade) => (
                            <option key={grade.id} value={grade.id}>
                              {labelOf(grade, langKey)}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      {showsSubjectFilter && (
                      <Col md={6}>
                        <Label className="form-label mb-1">
                          {t("subject")}
                        </Label>
                        <Input
                          type="select"
                          value={subjectId}
                          disabled={!gradeId}
                          onChange={(e) => setSubjectId(e.target.value)}
                        >
                          <option value="">{t("all")}</option>
                          {gradeSubjects.map((item: SelectListDto) => (
                            <option key={item.id} value={item.id}>
                              {labelOf(item, langKey)}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      )}
                        </>
                      )}
                    </Row>

                    {showsPurchaseFilter && (
                      <div className="mt-3">
                        <Label className="form-label mb-1">
                          {t("purchaseStatus")}
                        </Label>
                        <div className="d-flex flex-wrap gap-2">
                          {[
                            {
                              value: NotificationPurchaseFilterEnum.ALL,
                              label: t("all"),
                            },
                            {
                              value: NotificationPurchaseFilterEnum.PURCHASE,
                              label: t("withPurchase"),
                            },
                            {
                              value: NotificationPurchaseFilterEnum.NO_PURCHASE,
                              label: t("withoutPurchase"),
                            },
                          ].map((option) => (
                            <Button
                              key={option.value}
                              type="button"
                              color="secondary"
                              outline={
                                purchaseFilter !== option.value
                              }
                              className={`notify-choice-btn${
                                purchaseFilter === option.value
                                  ? " is-selected-success"
                                  : ""
                              }`}
                              onClick={() => setPurchaseFilter(option.value)}
                            >
                              {option.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}

                    {hasFilters && (
                      <div className="mt-2 text-end">
                        <Button
                          color="link"
                          size="sm"
                          className="p-0"
                          type="button"
                          onClick={clearTargetingFilters}
                        >
                          <i className="ri-close-circle-line me-1"></i>
                          {t("clearFilters")}
                        </Button>
                      </div>
                    )}

                    <div
                      className={`mt-4 rounded-3 p-3 d-flex align-items-center ${
                        receiversCount > 0 ? "bg-soft-success" : "bg-light"
                      }`}
                    >
                      <div className="avatar-sm flex-shrink-0 me-3">
                        <span
                          className={`avatar-title rounded-circle fs-20 ${
                            receiversCount > 0
                              ? "bg-success text-white"
                              : "bg-soft-secondary text-body"
                          }`}
                        >
                          {isCountingReceivers ? (
                            <Spinner size="sm" />
                          ) : (
                            <i className="ri-notification-3-line"></i>
                          )}
                        </span>
                      </div>
                      <div>
                        <div className="text-muted fs-12">
                          {t("receiversCount")}
                        </div>
                        <div className="fw-semibold fs-18 mb-0">
                          {audienceValues.length === 0
                            ? "—"
                            : isCountingReceivers
                              ? t("countingReceivers")
                              : receiversCount.toLocaleString()}
                        </div>
                        {audienceValues.length > 0 &&
                          !isCountingReceivers &&
                          !isReceiversCountError && (
                            <div className="text-muted fs-12">
                              {receiversCount > 0
                                ? t("youWillNotify", { count: receiversCount })
                                : t("noNotificationReceivers")}
                            </div>
                          )}
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xl={7}>
                <Card className="shadow-sm">
                  <CardHeader className="bg-light">
                    <h5 className="card-title mb-0">
                      <i className="ri-mail-send-line me-2 align-middle"></i>
                      {t("notificationContent")}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-3">
                      <label className="form-label">{t("method")}</label>
                      <div>
                        <div className="form-check form-check-inline">
                          <Input
                            className="form-check-input"
                            type="radio"
                            name="sendMethod"
                            id="methodPush"
                            value={NotificationMethodEnum.PUSH_NOTIFICATION}
                            checked={
                              sendMethod ==
                              NotificationMethodEnum.PUSH_NOTIFICATION
                            }
                            onChange={() => {
                              setSendMethod(
                                NotificationMethodEnum.PUSH_NOTIFICATION,
                              );
                            }}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="methodPush"
                          >
                            {t(NotificationMethodEnum.PUSH_NOTIFICATION)}
                          </label>
                        </div>
                      </div>
                    </div>

                    {sendMethod === NotificationMethodEnum.WHATSAPP && (
                      <div className="mb-3">
                        <TemplateSelect
                          placeholder="chooseTemplate"
                          isMulti={false}
                          closeMenuOnSelect={true}
                          value={selectedTemplate}
                          onChange={(newValue) => {
                            setSelectedTemplate(newValue);
                          }}
                        ></TemplateSelect>
                      </div>
                    )}

                    {sendMethod != NotificationMethodEnum.WHATSAPP && (
                      <Row>
                        <Col md={6} className="mb-3">
                          <Label htmlFor="title" className="form-label">
                            {t("title")}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            className="form-control"
                            id="title"
                            placeholder={t("enterTitle")}
                            value={notificationForm.values.title || ""}
                            onChange={notificationForm.handleChange}
                            onBlur={notificationForm.handleBlur}
                            invalid={
                              notificationForm.touched.title &&
                              notificationForm.errors.title
                                ? true
                                : false
                            }
                          />
                          {notificationForm.touched.title &&
                          notificationForm.errors.title ? (
                            <FormFeedback type="invalid">
                              {notificationForm.errors.title}
                            </FormFeedback>
                          ) : null}
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label htmlFor="arabicTitle" className="form-label">
                            {t("arabicTitle")}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            className="form-control"
                            id="arabicTitle"
                            placeholder={t("enterArabicTitle")}
                            value={notificationForm.values.arabicTitle || ""}
                            onChange={notificationForm.handleChange}
                            onBlur={notificationForm.handleBlur}
                            invalid={
                              notificationForm.touched.arabicTitle &&
                              notificationForm.errors.arabicTitle
                                ? true
                                : false
                            }
                          />
                          {notificationForm.touched.arabicTitle &&
                          notificationForm.errors.arabicTitle ? (
                            <FormFeedback type="invalid">
                              {notificationForm.errors.arabicTitle}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>
                    )}

                    <div className="mb-3">
                      <Label htmlFor="body" className="form-label">
                        {sendMethod === NotificationMethodEnum.WHATSAPP
                          ? t("template")
                          : t("body")}
                        {sendMethod != NotificationMethodEnum.WHATSAPP && (
                          <span className="text-danger">*</span>
                        )}
                      </Label>
                      <Input
                        disabled={
                          sendMethod === NotificationMethodEnum.WHATSAPP
                        }
                        type="textarea"
                        row="5"
                        style={{ height: "150px" }}
                        className="form-control"
                        id="body"
                        placeholder={t("enterMessageBody")}
                        value={notificationForm.values.body || ""}
                        onChange={notificationForm.handleChange}
                        onBlur={notificationForm.handleBlur}
                        invalid={
                          notificationForm.touched.body &&
                          notificationForm.errors.body
                            ? true
                            : false
                        }
                      />
                      {notificationForm.touched.body &&
                      notificationForm.errors.body ? (
                        <FormFeedback type="invalid">
                          {notificationForm.errors.body}
                        </FormFeedback>
                      ) : null}
                    </div>

                    {sendMethod != NotificationMethodEnum.WHATSAPP && (
                      <div className="mb-3">
                        <Label htmlFor="arabicBody" className="form-label">
                          {t("arabicBody")}
                          <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="textarea"
                          row="2"
                          className="form-control"
                          id="arabicBody"
                          placeholder={t("enterMessageBodyInArabic")}
                          value={notificationForm.values.arabicBody || ""}
                          onChange={notificationForm.handleChange}
                          onBlur={notificationForm.handleBlur}
                          invalid={
                            notificationForm.touched.arabicBody &&
                            notificationForm.errors.arabicBody
                              ? true
                              : false
                          }
                        />
                        {notificationForm.touched.arabicBody &&
                        notificationForm.errors.arabicBody ? (
                          <FormFeedback type="invalid">
                            {notificationForm.errors.arabicBody}
                          </FormFeedback>
                        ) : null}
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="form-label">
                        {t("chooseWhenToSend")}
                      </label>
                      <div>
                        <div className="form-check form-check-inline">
                          <Input
                            className="form-check-input"
                            type="radio"
                            name="occurrence"
                            id="sendNow"
                            value={OccurrenceEnum.NOW}
                            checked={nowOrLater === OccurrenceEnum.NOW}
                            onChange={() => {
                              setNowOrLater(OccurrenceEnum.NOW);
                              notificationForm.setFieldValue(
                                "scheduleDate",
                                new Date(),
                              );
                            }}
                          />
                          <label className="form-check-label" htmlFor="sendNow">
                            {t(OccurrenceEnum.NOW)}
                          </label>
                        </div>
                        <div className="form-check form-check-inline">
                          <Input
                            className="form-check-input"
                            type="radio"
                            name="occurrence"
                            id="sendLater"
                            checked={nowOrLater === OccurrenceEnum.LATER}
                            onChange={() => {
                              setNowOrLater(OccurrenceEnum.LATER);
                            }}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="sendLater"
                          >
                            {t(OccurrenceEnum.LATER)}
                          </label>
                        </div>
                      </div>
                    </div>

                    {nowOrLater === OccurrenceEnum.LATER && (
                      <div className="mb-3">
                        <label
                          htmlFor="scheduledNotification"
                          className="form-label"
                        >
                          {t("scheduleDate")}
                          <span className="text-danger">*</span>
                        </label>
                        <Flatpickr
                          id="scheduleDate"
                          onChange={(e) => onChangeDate(e)}
                          className="form-control"
                          value={notificationForm.values?.scheduleDate}
                          options={{
                            mode: "single",
                            enableTime: true,
                            dateFormat: "Y/m/d H:i",
                          }}
                        />
                        {notificationForm.touched.scheduleDate &&
                        notificationForm.errors.scheduleDate ? (
                          <FormFeedback
                            type="invalid"
                            style={{ display: "block" }}
                          >
                            {notificationForm.errors.scheduleDate as string}
                          </FormFeedback>
                        ) : null}
                      </div>
                    )}

                    <div className="hstack gap-2 justify-content-end">
                      <Link
                        to={"/notifications/list"}
                        className="btn btn-link link-success fw-medium"
                      >
                        {t("goToList")}
                      </Link>
                      <button
                        type="button"
                        className="btn btn-success"
                        id="add-btn"
                        disabled={
                          isLoading ||
                          isCountingReceivers ||
                          audienceValues.length === 0 ||
                          receiversCount <= 0
                        }
                        onClick={()=>{
                          console.log(notificationForm.errors);
                          onRequestSend()
                        }}
                      >
                        {isLoading ? (
                          <span className="d-flex align-items-center">
                            <span className="flex-grow-1 me-2">
                              {t("sending")}...
                            </span>
                            <Spinner
                              size="sm"
                              className="flex-shrink-0"
                              role="status"
                            >
                              {t("sending")}
                              ...
                            </Spinner>
                          </span>
                        ) : (
                          <>
                            {nowOrLater === OccurrenceEnum.NOW
                              ? t("send")
                              : t("later")}
                            {receiversCount > 0
                              ? ` (${receiversCount.toLocaleString()})`
                              : ""}
                          </>
                        )}
                      </button>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Form>
        </Container>
      </div>
      <ConfirmationModal
        show={showConfirm}
        loading={isLoading}
        icon={<i className="ri-notification-3-line display-4 text-success"></i>}
        title={t("confirmSendNotification")}
        description={t("confirmSendNotificationDescription", {
          count: receiversCount,
        })}
        confirmationButtonTitle={
          nowOrLater === OccurrenceEnum.NOW ? t("send") : t("later")
        }
        confirmationButtonInProgressTitle={t("sending")}
        onConfirmClick={() => notificationForm.handleSubmit()}
        onCloseClick={() => setShowConfirm(false)}
        error={
          isError && error && "data" in (error as any)
            ? ((error as any).data?.message as string)
            : null
        }
      />
    </React.Fragment>
  );
};

export default NotifyUsersPage;
